/**
 * UI-State.
 *
 * Hier liegen ausschliesslich fluechtige Oberflaechenzustaende. Alle echten
 * Daten kommen per `useLiveQuery` direkt aus Dexie - der Store spiegelt sie
 * bewusst nicht, sonst haette man zwei Wahrheiten.
 *
 * Persistiert wird nur ein kleiner Teil (Theme, Ansicht, Bereichsfilter).
 * Das ist eine reine Bequemlichkeit pro Browser; geht es verloren, startet die
 * App mit Standardwerten - kein Datenverlust.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { ID } from '@/domain/types';

export type Theme = 'light' | 'dark' | 'system';
/** `tage`: 2 bis 9 Tage ab dem Ankertag (F-04 a). */
export type CalendarViewMode = 'tag' | 'tage' | 'woche' | 'monat';

interface UiState {
  theme: Theme;
  calendarView: CalendarViewMode;
  /** Anzahl Tage der Ansicht `tage` (F-04 a). */
  calendarDays: number;
  /** Samstag und Sonntag ausblenden (F-04 e). */
  hideWeekends: boolean;
  /**
   * Leeres Array bedeutet "kein Filter aktiv, alles anzeigen".
   * Das ist absichtlich nicht "nichts anzeigen": ein frisch angelegter Bereich
   * wuerde sonst unsichtbar bleiben, bis man den Filter anfasst.
   */
  activeAreaIds: ID[];
  sidebarOpen: boolean;
  /** Zuletzt gewaehlte Farben, neueste zuerst (D-02) - je Geraet. */
  recentColors: string[];

  setTheme: (theme: Theme) => void;
  setCalendarView: (view: CalendarViewMode) => void;
  /** 1 = Tag, 7 = Woche, sonst `tage` mit n Tagen (Zifferntasten). */
  setCalendarDays: (n: number) => void;
  toggleWeekends: () => void;
  toggleArea: (id: ID) => void;
  clearAreaFilter: () => void;
  setSidebarOpen: (open: boolean) => void;
  rememberColor: (color: string) => void;
}

/** So viele zuletzt genutzte Farben stehen zur Schnellauswahl. */
export const RECENT_COLOR_COUNT = 8;

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: 'system',
      calendarView: 'woche',
      calendarDays: 3,
      hideWeekends: false,
      activeAreaIds: [],
      sidebarOpen: false,
      recentColors: [],

      setTheme: (theme) => set({ theme }),
      setCalendarView: (calendarView) => set({ calendarView }),
      setCalendarDays: (n) => set(
        n === 1 ? { calendarView: 'tag' } : n === 7 ? { calendarView: 'woche' } : { calendarView: 'tage', calendarDays: n },
      ),
      toggleWeekends: () => set((s) => ({ hideWeekends: !s.hideWeekends })),
      toggleArea: (id) =>
        set((s) => ({
          activeAreaIds: s.activeAreaIds.includes(id)
            ? s.activeAreaIds.filter((x) => x !== id)
            : [...s.activeAreaIds, id],
        })),
      clearAreaFilter: () => set({ activeAreaIds: [] }),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      rememberColor: (color) =>
        set((s) => ({
          recentColors: [color, ...s.recentColors.filter((c) => c !== color)].slice(0, RECENT_COLOR_COUNT),
        })),
    }),
    {
      name: 'ole-privat:ui',
      partialize: (s) => ({
        theme: s.theme,
        calendarView: s.calendarView,
        calendarDays: s.calendarDays,
        hideWeekends: s.hideWeekends,
        activeAreaIds: s.activeAreaIds,
        recentColors: s.recentColors,
      }),
    },
  ),
);
